import React from "react"
import { PageDrawerAction } from "src/components/ui/PageDrawerAction"
import { Icons } from "src/components/ui/Icons"
import { useActions, useAppState, useReaction } from "src/state"
import { 
    Guideline_Role_Enum,
    Guideline_Status_Enum,
    Guideline_Type_Enum,
    GuidelineFragment,
    Guidelines_Insert_Input,
    UpdateGuidelineMutationVariables
} from "src/state/gql/_types"
import { FormDrawer } from "src/components/forms/FormDrawer"
import { Loader } from "@mantine/core"
import { 
    OperationsAdminVenueGuidelineCreateFragment, 
    OperationsAdminVenueGuidelineCreateSchema, 
    OperationsAdminVenueGuidelineUpdateFragment, 
    OperationsAdminVenueGuidelineUpdateSchema 
} from "src/components/operations/administrator/guidelines/OperationsAdminVenueGuidelineSchemas"
import { extractCurrentItemData } from "src/utils/forms"

type FormPayloadType = OperationsAdminVenueGuidelineCreateFragment

let initialState: FormPayloadType = {
    name: "",
    description: "",
    tenant_required: false,
    venue_required: false,
    talent_required: false,
    document_id: null,
    metadata: null,
};

export const CreateOperationsAdminVenueGuidelineDrawer: React.FC<{onClose():void}> = ({ onClose }) => {

    const { createItem, updateItem, deleteItem } = useActions().guidelines;
    const { loading, errors } = useAppState().guidelines;
    const { tenant_id: currentTenantId } = useAppState(state => state.auth)
    
    let currentItemId = null;
    let currentItemData: FormPayloadType | null = null;

    const handleCreate = (payload: Guidelines_Insert_Input) => {
        return createItem({ venue_id: currentTenantId, type: Guideline_Type_Enum.Venue, parent_guideline_type: Guideline_Type_Enum.Venue, ...payload })
    }

    return (
        <>
            <FormDrawer<FormPayloadType, GuidelineFragment, string>
                autoOpen={false}
                formSchema={OperationsAdminVenueGuidelineCreateSchema}
                createAction={handleCreate}
                updateAction={updateItem}
                deleteAction={deleteItem}
                isLoading={loading}
                errors={errors}
                currentItemId={currentItemId}
                initialData={initialState}
                currentItemData={currentItemData}
                onClose={onClose}
                deleteRedirect={true}
            />

        </>
    )

}

export const CreateOperationsAdminVenueGuidelineAction = () => {
    return (
        <PageDrawerAction<GuidelineFragment>
            title='Add Guideline'
            action={CreateOperationsAdminVenueGuidelineDrawer}
            icon={Icons.add}
        />
    )
}

let initialStateUpdate: OperationsAdminVenueGuidelineUpdateFragment = {
    status: Guideline_Status_Enum.Active,
    role: Guideline_Role_Enum.Info,
    name: "",
    description: "",
    tenant_required: false,
    venue_required: false,
    talent_required: false,
    document_id: null,
    metadata: null,    
    target_booking_type: null,
    target_booking_status: null,
};

export const UpdateOperationsAdminVenueGuidelineDrawer: React.FC<{onClose():void,deselect:boolean}> = ({ onClose, deselect = true }) => {

    
    const { updateItem, deleteItem, deselectItem } = useActions().guidelines;
    const { currentItem, loading, errors } = useAppState().guidelines;

    if (loading || !currentItem) return <Loader />

    let currentItemId = null;
    let currentItemData: OperationsAdminVenueGuidelineUpdateFragment | null = null;
    if (currentItem) {
        currentItemId = currentItem.id;
        currentItemData = extractCurrentItemData<OperationsAdminVenueGuidelineUpdateFragment>(initialStateUpdate, currentItem);
    }

    const handleClose = () => {
        if(deselect) {
            deselectItem()
        }
        onClose()
    }

    const handleUpdate = (payload: UpdateGuidelineMutationVariables) => {
        return updateItem({
            id: payload.id,
            _set: {
                ...payload._set
            }
        })
    }

    return (
        <>
            <FormDrawer<OperationsAdminVenueGuidelineUpdateFragment, GuidelineFragment, string>
                autoOpen={false}
                formSchema={OperationsAdminVenueGuidelineUpdateSchema}
                updateAction={handleUpdate}
                deleteAction={deleteItem}
                isLoading={loading}
                errors={errors}
                currentItemId={currentItemId}
                initialData={initialStateUpdate}
                currentItemData={currentItemData}
                onClose={handleClose}
                deleteRedirect={true}
            />

        </>
    )

}

export const UpdateOperationsAdminVenueGuidelineAction = () => {

    const reaction = useReaction()
    const { deselectItem } = useActions().guidelines;
    const [opened, setOpened] = React.useState(false);

    React.useEffect(() => reaction(
        (state) => state.guidelines.currentItemId,
        (selectedID) => {
            setOpened(!!selectedID)
        }, { immediate: false }
    ))

    const handleClose = () => {
        deselectItem()
    }

    return (
        <PageDrawerAction<GuidelineFragment>
            title='Edit Guideline'
            action={UpdateOperationsAdminVenueGuidelineDrawer}
            icon={Icons.edit}
            startOpened={opened}
            onClose={handleClose}
            hidden
        />
    )
}

export const EditOperationsAdminVenueGuidelineAction = () => {
    return (
        <PageDrawerAction<GuidelineFragment>
            title='Edit Guideline'
            action={UpdateOperationsAdminVenueGuidelineDrawer}
            icon={Icons.edit}
            props={{
                deselect: false
            }}
        />
    )
}